import { useEffect, useState } from "react";
import { Download, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * SAMARA PWA + Service-Worker Update Manager.
 *
 * Two responsibilities:
 *   1. "Install SAMARA" bottom-sheet on Android/iOS browsers that support the
 *      `beforeinstallprompt` event.
 *   2. Register `/sw.js` and — whenever a **new** service worker is installed
 *      while an old one is still controlling the page — surface a subtle
 *      "New version available · Reload" toast. Tapping the toast posts
 *      `SKIP_WAITING` to the new SW and reloads the tab exactly once.
 *
 * The auto-reload used to fire silently on `controllerchange`. That was fine
 * for the one-off v1 → v3 migration but from now on we let the user opt in so
 * mid-checkout customers don't get their cart nuked by a background reload.
 */
export default function PWAInstall() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      if (!localStorage.getItem("samara_pwa_dismissed")) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if ("serviceWorker" in navigator) {
      let reloaded = false;
      const doReload = (nw) => {
        if (reloaded) return;
        reloaded = true;
        // Ask the freshly-installed SW to take over immediately, then reload
        // as soon as it becomes the active controller.
        if (nw) nw.postMessage({ type: "SKIP_WAITING" });
      };

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!reloaded) return;   // only reload if user consented via the toast
        window.location.reload();
      });

      navigator.serviceWorker.register("/sw.js").then((reg) => {
        // A waiting worker means an update was found on a previous session but
        // never activated — surface the toast right away.
        if (reg.waiting && navigator.serviceWorker.controller) {
          showUpdateToast(() => doReload(reg.waiting));
        }
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // A new SW was installed while an OLD one is still in charge
                // → true update. Ask the user to reload.
                showUpdateToast(() => doReload(nw));
              }
              // else: first-ever SW install for this device. No toast, no reload.
            }
          });
        });
      }).catch(() => {});
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!show || !prompt) return null;
  return (
    <div data-testid="pwa-install-card" className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 z-50 max-w-sm bg-navy text-white p-5 rounded-sm shadow-2xl border border-gold/30">
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-1">Install SAMARA</div>
          <p className="text-sm text-white/80">Add to your home screen for an app-like experience.</p>
        </div>
        <button data-testid="pwa-dismiss" onClick={() => { setShow(false); localStorage.setItem("samara_pwa_dismissed", "1"); }} className="text-white/60 hover:text-white"><X size={16} /></button>
      </div>
      <button data-testid="pwa-install-btn" onClick={async () => {
        prompt.prompt();
        await prompt.userChoice;
        setShow(false);
      }} className="mt-4 w-full bg-gold text-navy py-2 rounded-sm inline-flex items-center justify-center gap-2 font-medium text-sm">
        <Download size={14} /> Install app
      </button>
    </div>
  );
}

/** Silent SW update — user directive "remove message for reload, bad impression".
 *  Newly-installed workers activate quietly; the fresh shell will render on the
 *  user's *next* navigation. No toast, no reload prompt, no interruption.
 */
function showUpdateToast(_onReload) {
  // intentionally no-op
}
